class MobileDock extends HTMLElement {
  constructor() {
    super();
    this.lastScrollY = 0;
    this.scrollThreshold = 50;
    this.ticking = false;
    this.lastScrollForHeaderFix = 0;
  }

  connectedCallback() {
    const lic = (document.cookie.match(/inspd_s=(\d)/) || [])[1];
    if ((lic === '0' || lic === '2') && !(window.Shopify && Shopify.designMode)) {
      this.style.display = 'none';
      return;
    }
    const header = document.querySelector('.shopify-section-header-group, .header-wrapper, #shopify-section-header');

    this.isAction = false;
    this.scrollY = header ? parseInt(header.getBoundingClientRect().bottom) : 0;
    this.behavior = this.dataset.behavior || 'scroll_up';

    if (Shopify && Shopify.designMode) {
      this.scrollY = 0;
      this.classList.add('is-active');
    }

    // Attach click handlers to action buttons
    this.querySelectorAll('.dock__item[data-action]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });

    // Listen for drawer close events
    document.addEventListener('menudrawer:close', this.hideStickyHeader.bind(this));
    document.addEventListener('searchmodal:close', this.hideStickyHeader.bind(this));

    // Handle scroll with proper behavior logic
    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    this.onScrollHandler();

    // Set CSS variable for mobile dock height
    document.documentElement.style.setProperty('--mobile-dock-height', `${this.offsetHeight}px`);
    
    // Update height on resize
    window.addEventListener('resize', () => {
      if (window.innerWidth < 750) {
        document.documentElement.style.setProperty('--mobile-dock-height', `${this.offsetHeight}px`);
      }
    });

    // Apply behavior-specific sticky header handling
    this.initializeStickyHeaderBehavior();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  initializeStickyHeaderBehavior() {
    const stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader || window.innerWidth >= 750) return;

    if (this.behavior === 'always') {
      if (stickyHeader.hide) {
        stickyHeader.hide();
      }
    }
  }

  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateDockAndHeaderVisibility();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateDockAndHeaderVisibility() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const stickyHeader = document.querySelector('sticky-header');

    if (window.innerWidth >= 750) return;

    if (this.behavior === 'always') {
      if (scrollTop >= this.scrollY) {
        this.classList.add('is-active');
        if (stickyHeader && stickyHeader.hide) {
          stickyHeader.hide();
        }
      } else {
        this.classList.remove('is-active');
      }
    } else {
      if (scrollTop < this.scrollY) {
        this.classList.remove('is-active');
        if (stickyHeader && stickyHeader.hide) {
          stickyHeader.hide();
        }
      } else {
        const isScrollingUp = scrollTop < this.lastScrollY;
        const isScrollingDown = scrollTop > this.lastScrollY + this.scrollThreshold;

        if (isScrollingUp) {
          this.classList.add('is-active');
          if (stickyHeader && stickyHeader.hide) {
            stickyHeader.hide();
          }
        } else if (isScrollingDown) {
          this.classList.remove('is-active');
          if (stickyHeader && stickyHeader.reveal) {
            stickyHeader.reveal();
          }
        }
      }
    }

    this.lastScrollY = scrollTop;
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    switch (target.dataset.action) {
      case 'cart':
        this.isAction = true;

        const miniCart = document.querySelector('mini-cart');
        if (miniCart) {
          miniCart.open();
          document.activeElement.blur();
        }
        else {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
            document.activeElement.blur();
          }
          else {
            window.location.href = window.Shopify?.routes?.cart || '/cart';
          }
        }
        break;

      case 'menu':
        event.stopPropagation();
        this.isAction = true;

        this.ensureHeaderVisibility(() => {
          const headerDrawer = document.querySelector('header-drawer');
          if (headerDrawer) {
            const summary = headerDrawer.querySelector('summary');
            if (summary) {
              summary.click();
            }
          }
        });
        break;

      case 'search':
        event.stopPropagation();
        this.isAction = true;
        
        this.ensureHeaderVisibility(() => {
          const searchModals = document.querySelectorAll('search-modal');
          if (searchModals.length > 0) {
            // The Search modal section is display:none until it opens, so don't
            // test computed display here — open the first connected instance.
            const searchModal = searchModals[0];
            if (typeof searchModal.open === 'function') {
              searchModal.lastTrigger = target;
              searchModal.open();
            }
          }
          else {
            const searchModal = document.querySelector('details-modal#header-search');
            if (searchModal) {
              const details = searchModal.querySelector('details');
              if (details) {
                details.setAttribute('open', '');
                const input = details.querySelector('input:not([type="hidden"])');
                if (input) {
                  setTimeout(() => input.focus(), 100);
                }
              }
            }
            else {
              window.location.href = window.Shopify?.routes?.search || '/search';
            }
          }
        });
        break;
    }
  }

  ensureHeaderVisibility(callback) {
    const header = document.querySelector(
      '.shopify-section-group-header-group[id*="__header"], #shopify-section-header, .header-wrapper'
    );
    
    if (!header) {
      callback();
      return;
    }

    const rect = header.getBoundingClientRect();
    const isHeaderVisible = rect.bottom > 0 && rect.top < window.innerHeight;

    if (isHeaderVisible) {
      callback();
      return;
    }

    const originalPosition = header.style.position;
    const originalTop = header.style.top;
    const originalLeft = header.style.left;
    const originalWidth = header.style.width;
    const originalZIndex = header.style.zIndex;
    const originalTransition = header.style.transition;
    const originalTransform = header.style.transform;
    
    header.style.position = 'fixed';
    header.style.top = '0';
    header.style.left = '0';
    header.style.width = '100%';
    header.style.zIndex = '1000';
    header.setAttribute('data-mobile-dock-fixed', 'true');

    const restoreHeader = () => {
      header.style.transition = 'transform 0.3s ease-out';
      header.style.transform = 'translateY(-100%)';

      const onTransitionDone = () => {
        header.style.position = originalPosition;
        header.style.top = originalTop;
        header.style.left = originalLeft;
        header.style.width = originalWidth;
        header.style.zIndex = originalZIndex;
        header.style.transition = originalTransition;
        header.style.transform = originalTransform;
        header.removeAttribute('data-mobile-dock-fixed');
        header.removeEventListener('transitionend', onTransitionDone);
      };

      header.addEventListener('transitionend', onTransitionDone, { once: true });
      setTimeout(onTransitionDone, 350);
    };
    
    setTimeout(() => {
      callback();

      // Watch for the drawer/modal to close by observing the details element.
      // MenuDrawer.closeMenuDrawer does not dispatch a custom event,
      // so we observe when the open attribute is removed from <details>.
      const openDetails = header.querySelector('details[open]');
      if (openDetails) {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.attributeName === 'open' && !openDetails.hasAttribute('open')) {
              restoreHeader();
              observer.disconnect();
              return;
            }
          }
        });
        observer.observe(openDetails, { attributes: true, attributeFilter: ['open'] });
        return;
      }

      // Fallback: no open details found, use scroll-to-hide
      const hideOnScroll = () => {
        if (Math.abs(window.pageYOffset - this.lastScrollForHeaderFix) > 10) {
          header.style.transition = 'transform 0.3s ease-out';
          header.style.transform = 'translateY(-100%)';
          
          setTimeout(() => {
            restoreHeader();
          }, 300);
          
          window.removeEventListener('scroll', hideOnScroll);
        }
      };
      
      this.lastScrollForHeaderFix = window.pageYOffset || document.documentElement.scrollTop;
      window.addEventListener('scroll', hideOnScroll, { passive: true });
    }, 50);
  }

  hideStickyHeader() {
    const header = document.querySelector('sticky-header');
    if (!header) return;

    const isSmallScreen = window.innerWidth < 750;
    
    if (isSmallScreen && this.isAction && header.hide) {
      setTimeout(() => {
        header.hide();
        this.isAction = false;
      }, 500);
    }
  }
}

customElements.define('mobile-dock', MobileDock);